import { supabase } from '@/lib/supabase/client';

export interface AuthenticatedFetchOptions extends RequestInit {
  timeout?: number;
  /**
   * When true, do not auto-throw on non-OK responses. The caller receives the raw
   * Response and is expected to handle errors itself (e.g. via problem+json unwrap).
   * Pipeline orchestrator endpoints opt in; legacy callers keep the auto-throw.
   */
  rawResponse?: boolean;
}

export class APIError extends Error {
  constructor(
    message: string,
    public status: number,
    public statusText: string,
    public response?: any
  ) {
    super(message);
    this.name = 'APIError';
  }
}

/**
 * Creates a fetch function that automatically includes Supabase JWT token
 * in the Authorization header and handles timeouts.
 */
export async function authenticatedFetch(
  url: string,
  options: AuthenticatedFetchOptions = {}
): Promise<Response> {
  const { timeout = 30000, rawResponse = false, ...fetchOptions } = options;

  // Get Supabase session token
  const {
    data: { session },
    error: sessionError
  } = await supabase.auth.getSession();

  if (sessionError) {
    throw new APIError('Failed to get authentication session', 401, 'Unauthorized', sessionError);
  }

  // Prepare headers
  const headers = new Headers(fetchOptions.headers);

  // Add authorization header if we have a session
  if (session?.access_token) {
    headers.set('Authorization', `Bearer ${session.access_token}`);
  }

  // Ensure Content-Type is set for JSON requests
  if (fetchOptions.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    // Handle non-OK responses (skip when caller opts into rawResponse)
    if (!rawResponse && !response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { error: response.statusText };
      }

      throw new APIError(
        errorData.error || errorData.message || `Request failed: ${response.statusText}`,
        response.status,
        response.statusText,
        errorData
      );
    }

    return response;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof APIError) {
      throw error;
    }

    if (error instanceof Error && error.name === 'AbortError') {
      throw new APIError('Request timeout', 408, 'Request Timeout', { timeout });
    }

    // Check for CORS errors specifically
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      // This is likely a CORS error
      const isCorsError = !error.message.includes('timeout') && !error.message.includes('network');

      if (isCorsError) {
        throw new APIError(
          'CORS Error: The backend service is not configured to allow requests from this origin. Please contact the backend team to add your origin to the CORS allowed origins list.',
          0,
          'CORS Error',
          {
            originalError: error.message,
            url,
            isCorsError: true
          }
        );
      }
    }

    throw new APIError(
      error instanceof Error ? error.message : 'Network request failed',
      0,
      'Network Error',
      error
    );
  }
}
